<script setup lang="ts">
import maplibregl from 'maplibre-gl'
import { inject, onUnmounted, watch, type ShallowRef } from 'vue'

/**
 * Pure-visual overlay for the in-progress drawing track. Renders only:
 *   - the dashed in-progress line,
 *   - an invisible wide hit-line used by useTrackDrag for line-drag reroute.
 *
 * Anchor / route-point markers and the routing spinner are intentionally
 * NOT rendered here — `RoutePointLayer` is the single source of truth for
 * route-point markers (persisted + pending) and the spinner for the workspace
 * routing flow.
 */

const props = withDefaults(
  defineProps<{
    coordinates: [number, number][] // [lon, lat]
    color?: string
    width?: number
    showLine?: boolean
  }>(),
  {
    showLine: true,
  },
)

const mapInstance = inject<ShallowRef<maplibregl.Map | null>>('mapInstance')
const SOURCE_ID = 'drawing-track-source'
const LAYER_ID = 'drawing-track-layer'
const TOUCH_TARGET_LAYER_ID = 'drawing-track-touch-target'

const updateLineLayer = () => {
  if (!mapInstance?.value) return
  const map = mapInstance.value
  const source = map.getSource(SOURCE_ID) as maplibregl.GeoJSONSource | undefined

  const lineFeature: GeoJSON.Feature<GeoJSON.LineString> = {
    type: 'Feature',
    properties: {},
    geometry: {
      type: 'LineString',
      coordinates: props.coordinates,
    },
  }

  const data: GeoJSON.FeatureCollection = {
    type: 'FeatureCollection',
    features: [lineFeature],
  }

  if (source) {
    source.setData(data)
  } else {
    map.addSource(SOURCE_ID, { type: 'geojson', data })

    map.addLayer({
      id: TOUCH_TARGET_LAYER_ID,
      type: 'line',
      source: SOURCE_ID,
      filter: ['==', '$type', 'LineString'],
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: { 'line-color': 'transparent', 'line-width': 20 },
    })

    map.addLayer({
      id: LAYER_ID,
      type: 'line',
      source: SOURCE_ID,
      filter: ['==', '$type', 'LineString'],
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: {
        'line-color': props.color ?? '#3b82f6',
        'line-width': props.width ?? 3,
        'line-dasharray': [3, 2],
        'line-opacity': props.showLine === false ? 0 : 1,
      },
    })
  }

  if (map.getLayer(LAYER_ID)) {
    map.setPaintProperty(LAYER_ID, 'line-color', props.color ?? '#3b82f6')
    map.setPaintProperty(LAYER_ID, 'line-width', props.width ?? 3)
    map.setPaintProperty(LAYER_ID, 'line-opacity', props.showLine === false ? 0 : 1)
  }
}

watch(() => props.coordinates, updateLineLayer, { deep: true })
watch(() => props.color, updateLineLayer)
watch(() => props.width, updateLineLayer)
watch(() => props.showLine, updateLineLayer)

watch(
  () => mapInstance?.value,
  (map) => {
    if (map) {
      if (map.loaded()) {
        updateLineLayer()
      } else {
        map.on('load', updateLineLayer)
      }
    }
  },
  { immediate: true },
)

onUnmounted(() => {
  if (mapInstance?.value) {
    const map = mapInstance.value
    if (map.getLayer(LAYER_ID)) map.removeLayer(LAYER_ID)
    if (map.getLayer(TOUCH_TARGET_LAYER_ID)) map.removeLayer(TOUCH_TARGET_LAYER_ID)
    if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID)
  }
})
</script>

<template>
  <slot></slot>
</template>
